import {Formik} from 'formik';

import {FormComponent} from './FormComponent';

import {RecordSchema} from './RecordSchema';

import {FormWithButtons} from '@flexinet/ui-components';

import {PaymentIntent} from '../types';


export const RecordForm = ({
                               record,
                               handleSubmit,
                               handleCancel,
                           }: {
    record: Omit<PaymentIntent, 'id'>,
    handleSubmit: (values: PaymentIntent) => void | Promise<unknown>,
    handleCancel?: () => void
}) => {

    /**
     * ⚠️ MONEY PATH — `await`, and do NOT swallow the rejection.
     *
     * This was `handleSubmit(values)` with no await and a `void` return type,
     * so Formik's promise resolved on the same tick: `isSubmitting` flipped
     * back to false immediately and `disabled={isSubmitting}` on the submit
     * button never engaged for the duration of the request. On a form that
     * CREATES A PAYMENT INTENT, that is a double-click away from a second
     * charge — not merely a duplicate row.
     *
     * The rejection is deliberately NOT caught. `usePaymentIntent` already
     * notifies through the mutation's `onError`; catching here as well would
     * both double-notify and tell Formik the submit succeeded, which closes the
     * drawer over a failed mutation. Letting it propagate keeps the form open
     * with the operator's values intact.
     */
    const onSubmit = async (values: PaymentIntent) => {
        await handleSubmit(values);
    };

    const onCancel = () => {
        handleCancel?.();
    };


    let initialValues = {
        amount: 0,
        currency: 'usd',
        description: '',
        customerId: '',
        automaticPaymentMethods: {
            enabled: true,
        },
        setupFutureUsage: 'offSession',
    } as PaymentIntent;

    if (record) {
        initialValues = Object.assign({}, initialValues, record);
    }

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={RecordSchema}
            onSubmit={onSubmit}
        >
            {({
                  handleSubmit,
                  isSubmitting,
                  ...rest
              }) => (
                <FormWithButtons handleSubmit={handleSubmit} isSubmitting={isSubmitting}
                                 handleCancel={onCancel} debug={false} {...rest}  >
                    <FormComponent/>
                </FormWithButtons>)}
        </Formik>
    );
};
